'use client'

import React, { useState, useEffect, useRef } from 'react'
import { Button } from '@/shared/components/ui/button'
import { Camera, PlusIcon } from 'lucide-react'
import {
  getPhotosByGroupId,
  updateGroupPhoto,
  deleteGroupPhoto,
  type GroupPhoto,
} from '@/features/group-photos/api/group-photos.api'
import { uploadMultipleGroupPhotosDirect } from '@/features/group-photos/upload-client'
import { useSession } from '@/features/auth/context/session.context'
import { authFetchJson } from '@/shared/lib/auth-fetch'
import { UserRole } from '@/shared/constants/user-roles'
import { downloadPhotoOrVideo } from '@/features/photos/lib/download-media'
import {
  GroupPhotosTimelineTabContent,
  type TimelineDay,
  type CapturedPhotoForContent,
} from './GroupPhotosTimelineTabContent'

const CONFIDENTIAL_TAG = 'Confidential'

type GroupPhotosTimelineTabProps = {
  groupId: string
}

export default function GroupPhotosTimelineTab({ groupId }: GroupPhotosTimelineTabProps) {
  const { session } = useSession()
  const currentUserId = session?.sub != null ? Number(session.sub) : null
  const isVolunteerRole = session?.role === UserRole.VOLUNTEER
  const isTeamLeaderRole = session?.role === UserRole.TEAM_LEADER
  const canDownload = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [preSelectedFiles, setPreSelectedFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedPhoto, setSelectedPhoto] = useState<GroupPhoto | null>(null)
  const [selectedVideo, setSelectedVideo] = useState<GroupPhoto | null>(null)
  const [pendingViewerDescription, setPendingViewerDescription] = useState<string | undefined>(undefined)
  const [isViewerVoiceOpen, setIsViewerVoiceOpen] = useState(false)
  const [isViewerTextOpen, setIsViewerTextOpen] = useState(false)
  const [isViewerTranscribing, setIsViewerTranscribing] = useState(false)
  const [photoTagFilter, setPhotoTagFilter] = useState<string | null>(null) // 'Confidential' to filter timeline
  const [photos, setPhotos] = useState<GroupPhoto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  interface CapturedPhoto extends CapturedPhotoForContent {
    description?: string | null
    uploadedPhotoId?: number
  }

  const [isCameraOpen, setIsCameraOpen] = useState(false)
  const [capturedPhotos, setCapturedPhotos] = useState<CapturedPhoto[]>([])
  const [showPostCapturePopup, setShowPostCapturePopup] = useState(false)
  const [currentPhotoTempId, setCurrentPhotoTempId] = useState<string | null>(null)
  const [lastSavedThumbnailUrl, setLastSavedThumbnailUrl] = useState<string | null>(null)
  const [isVoiceRecordingOpen, setIsVoiceRecordingOpen] = useState(false)
  const [isTextInputOpen, setIsTextInputOpen] = useState(false)
  const [uploadingPhotos, setUploadingPhotos] = useState<Set<string>>(new Set())
  const isTextInputSavingRef = useRef(false)
  const isCameraOpenRef = useRef(isCameraOpen)
  const isTextInputOpenRef = useRef(isTextInputOpen)
  const isVoiceRecordingOpenRef = useRef(isVoiceRecordingOpen)
  const currentTakenAtRef = useRef<string>(new Date().toISOString())

  useEffect(() => {
    isCameraOpenRef.current = isCameraOpen
  }, [isCameraOpen])
  useEffect(() => {
    isTextInputOpenRef.current = isTextInputOpen
  }, [isTextInputOpen])
  useEffect(() => {
    isVoiceRecordingOpenRef.current = isVoiceRecordingOpen
  }, [isVoiceRecordingOpen])

  useEffect(() => {
    if (!isVolunteerRole) return
    if (photoTagFilter !== CONFIDENTIAL_TAG) return
    setPhotoTagFilter(null)
  }, [isVolunteerRole, photoTagFilter])

  useEffect(() => {
    let isCancelled = false
    const fetchData = async () => {
      if (!groupId) {
        if (!isCancelled) {
          setPhotos([])
          setIsLoading(false)
        }
        return
      }
      try {
        if (!isCancelled) setIsLoading(true)
        if (!isCancelled) setError(null)
        const res = await getPhotosByGroupId(groupId, {
          includeDeleted: false,
          ...(photoTagFilter && (!isVolunteerRole || photoTagFilter !== CONFIDENTIAL_TAG) && { tags: [photoTagFilter] }),
        })
        if (!isCancelled) {
          if (res.success) {
            const list = Array.isArray(res.data) ? res.data : []
            const visible = isVolunteerRole
              ? list.filter((p) => !(p.tags ?? []).includes(CONFIDENTIAL_TAG))
              : list
            setPhotos(visible)
          } else {
            setError(res.message ?? 'Failed to fetch photos')
            setPhotos([])
          }
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error')
          setPhotos([])
        }
      } finally {
        if (!isCancelled) setIsLoading(false)
      }
    }
    fetchData()
    return () => {
      isCancelled = true
    }
  }, [groupId, refreshKey, photoTagFilter, isVolunteerRole])

  const handleUploadSuccess = () => {
    setRefreshKey((prev) => prev + 1)
    setPreSelectedFiles([])
  }

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files?.length) {
      setPreSelectedFiles(Array.from(files))
      setIsModalOpen(true)
    }
    e.target.value = ''
  }

  const getTimelineDays = (): TimelineDay[] => {
    const map = new Map<string, GroupPhoto[]>()
    photos.forEach((photo) => {
      const dateToUse = photo.takenAt || photo.uploadedAt
      const date = new Date(dateToUse).toDateString()
      if (!map.has(date)) map.set(date, [])
      map.get(date)!.push(photo)
    })
    return Array.from(map.entries())
      .map(([date, dayPhotos]) => ({
        date,
        displayDate: formatDisplayDate(date),
        photos: dayPhotos.sort((a, b) => {
          const tA = new Date(a.takenAt || a.uploadedAt).getTime()
          const tB = new Date(b.takenAt || b.uploadedAt).getTime()
          return tB - tA
        }),
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }

  function formatDisplayDate(dateString: string) {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    if (date.toDateString() === today.toDateString()) return 'Today'
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  function formatDate(dateString: string | null) {
    if (!dateString) return 'Unknown date'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedPhoto) setSelectedPhoto(null)
        if (selectedVideo) setSelectedVideo(null)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [selectedPhoto, selectedVideo])

  const handlePhotoClick = (photo: GroupPhoto) => {
    if (photo.mediaType === 'video') {
      setSelectedVideo(photo)
      setSelectedPhoto(null)
    } else {
      setSelectedPhoto(photo)
      setSelectedVideo(null)
    }
  }

  const closePhotoViewer = () => {
    setSelectedPhoto(null)
    setPendingViewerDescription(undefined)
    setIsViewerVoiceOpen(false)
    setIsViewerTextOpen(false)
    setIsViewerTranscribing(false)
  }

  const closeVideoPlayer = () => setSelectedVideo(null)

  const transcribeAudioBlob = async (
    audioBlob: Blob
  ): Promise<{ success: true; text: string } | { success: false; message: string }> => {
    const audioFile = new File([audioBlob], 'recording.webm', {
      type: audioBlob.type || 'audio/webm',
    })
    const formData = new FormData()
    formData.append('audio', audioFile)
    const result = await authFetchJson<any>('/ai/transcribe', {
      method: 'POST',
      body: formData,
    })
    if (!result.success) {
      return {
        success: false,
        message: result.message || 'Transcription failed',
      }
    }
    if (result.data?.transcription != null) {
      return { success: true, text: String(result.data.transcription) }
    }
    return { success: false, message: 'No transcription returned' }
  }

  const handleViewerVoiceSave = async (audioBlob: Blob) => {
    setIsViewerVoiceOpen(false)
    if (!audioBlob || audioBlob.size < 100) {
      alert('No audio was recorded. Please try again.')
      return
    }
    if (!selectedPhoto) return
    setIsViewerTranscribing(true)
    try {
      const outcome = await transcribeAudioBlob(audioBlob)
      if (outcome.success) {
        const existing = (selectedPhoto.description ?? '').trim()
        const newDescription = existing ? `${existing} ${outcome.text}` : outcome.text
        setPendingViewerDescription(newDescription)
        const result = await updateGroupPhoto(selectedPhoto.id, { description: newDescription })
        if (result.success) {
          setSelectedPhoto((prev) => (prev ? { ...prev, description: newDescription } : null))
          setPendingViewerDescription(undefined)
          setRefreshKey((prev) => prev + 1)
        } else {
          alert(`Failed to save description: ${result.message ?? 'Unknown error'}`)
        }
      } else {
        alert(`Voice transcription failed: ${outcome.message}. Try Keyboard to type a description.`)
      }
    } catch (err) {
      alert(`Voice transcription error: ${err instanceof Error ? err.message : 'Unknown error'}. Try Keyboard.`)
    } finally {
      setIsViewerTranscribing(false)
    }
  }

  const handleViewerTextSave = async (text: string) => {
    setIsViewerTextOpen(false)
    if (!selectedPhoto) return
    setPendingViewerDescription(text)
    try {
      const result = await updateGroupPhoto(selectedPhoto.id, { description: text })
      if (result.success) {
        setSelectedPhoto((prev) => (prev ? { ...prev, description: text } : null))
        setPendingViewerDescription(undefined)
        setRefreshKey((prev) => prev + 1)
      } else {
        alert(`Failed to save description: ${result.message ?? 'Unknown error'}`)
      }
    } catch {
      alert('An error occurred while saving the description')
    }
  }

  const handleDeletePhoto = async (photoId: number, event: React.MouseEvent) => {
    event.stopPropagation()
    if (!confirm('Are you sure you want to delete this photo?')) return
    try {
      const result = await deleteGroupPhoto(photoId)
      if (result.success) setRefreshKey((prev) => prev + 1)
      else alert(`Failed to delete: ${result.message ?? 'Unknown error'}`)
    } catch {
      alert('An error occurred while deleting')
    }
  }

  const canDeletePhoto = (photo: GroupPhoto | null | undefined): boolean => {
    if (!photo) return false
    if (!isVolunteerRole) return true
    if (currentUserId == null) return false
    return photo.uploadedByUserId != null && Number(photo.uploadedByUserId) === Number(currentUserId)
  }

  const canEditDescription = (photo: GroupPhoto | null | undefined): boolean => {
    if (!photo) return false
    if (!isVolunteerRole && !isTeamLeaderRole) return true
    if (currentUserId == null) return false
    return photo.uploadedByUserId != null && Number(photo.uploadedByUserId) === Number(currentUserId)
  }

  const uploadPhotoImmediately = async (
    photoTempId: string,
    description?: string | null,
    overridePhoto?: CapturedPhoto,
    takenAt?: string | null
  ): Promise<number | undefined> => {
    const photo = overridePhoto ?? capturedPhotos.find((p) => p.tempId === photoTempId)
    const takenAtToUse = takenAt ?? photo?.takenAt ?? overridePhoto?.takenAt
    if (!photo || !groupId) return undefined
    if (uploadingPhotos.has(photoTempId)) return undefined
    setUploadingPhotos((prev) => new Set(prev).add(photoTempId))
    try {
      const fileToUpload = new File([photo.file], photo.file.name, {
        type: photo.file.type,
        lastModified: photo.file.lastModified,
      })
      const result = await uploadMultipleGroupPhotosDirect(groupId, [fileToUpload])
      if (!result.success) {
        throw new Error(result.message ?? 'Upload failed')
      }
      if (!result.data?.length) {
        throw new Error('Upload failed')
      }
      const uploaded = result.data[0]
      if (description || takenAtToUse) {
        await updateGroupPhoto(uploaded.id, {
          ...(description && { description }),
          ...(takenAtToUse && { takenAt: takenAtToUse }),
        })
      }
      if (photo.thumbnailUrl) setLastSavedThumbnailUrl(photo.thumbnailUrl)
      setCapturedPhotos((prev) => prev.filter((p) => p.tempId !== photoTempId))
      URL.revokeObjectURL(photo.url)
      return uploaded.id
    } catch (err) {
      console.error('Error uploading group photo:', err)
      alert(err instanceof Error ? err.message : 'Failed to upload')
      return undefined
    } finally {
      setUploadingPhotos((prev) => {
        const next = new Set(prev)
        next.delete(photoTempId)
        return next
      })
    }
  }

  const handleCameraCapture = (file: File) => {
    if (file.size === 0) {
      alert('Error: The captured file is empty. Please try again.')
      return
    }
    const url = URL.createObjectURL(file)
    const tempId = `photo-${Date.now()}-${Math.random()}`
    const isVideo = file.type.startsWith('video/')
    if (!isVideo) setCurrentPhotoTempId(tempId)
    if (isVideo) {
      const video = document.createElement('video')
      video.src = url
      video.muted = true
      video.playsInline = true
      video.preload = 'metadata'
      let videoAdded = false
      const addVideo = (thumbUrl: string | null) => {
        if (videoAdded) return
        videoAdded = true
        const newPhoto: CapturedPhoto = {
          file,
          url,
          thumbnailUrl: thumbUrl,
          takenAt: new Date().toISOString(),
          tempId,
        }
        setCapturedPhotos((prev) => [...prev, newPhoto])
        setLastSavedThumbnailUrl(null)
        uploadPhotoImmediately(tempId, null, newPhoto).catch(console.error)
      }
      const timeoutId = setTimeout(() => {
        if (!videoAdded) addVideo(null)
      }, 5000)
      video.onloadedmetadata = () => {
        try {
          video.currentTime = Math.min(1, Math.max(0.1, video.duration * 0.1)) || 0.1
        } catch {
          video.currentTime = 0
        }
      }
      video.onseeked = () => {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = 48
          canvas.height = 48
          const ctx = canvas.getContext('2d')
          if (ctx && video.videoWidth > 0 && video.videoHeight > 0) {
            const scale = Math.max(48 / video.videoWidth, 48 / video.videoHeight)
            const x = (48 - video.videoWidth * scale) / 2
            const y = (48 - video.videoHeight * scale) / 2
            ctx.drawImage(video, x, y, video.videoWidth * scale, video.videoHeight * scale)
            canvas.toBlob((blob) => {
              if (blob) addVideo(URL.createObjectURL(blob))
              else addVideo(null)
            }, 'image/jpeg', 0.8)
          } else addVideo(null)
        } catch {
          addVideo(null)
        }
        clearTimeout(timeoutId)
      }
      video.onerror = () => {
        clearTimeout(timeoutId)
        if (!videoAdded) addVideo(null)
      }
      video.load()
    } else {
      const addPhoto = (thumbUrl: string | null) => {
        const newPhoto: CapturedPhoto = {
          file,
          url,
          thumbnailUrl: thumbUrl,
          takenAt: new Date().toISOString(),
          tempId,
        }
        setCapturedPhotos((prev) => [...prev, newPhoto])
        setLastSavedThumbnailUrl(null)
        setShowPostCapturePopup(true)
      }
      const img = document.createElement('img')
      img.onerror = () => addPhoto(null)
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = 48
        canvas.height = 48
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          addPhoto(null)
          return
        }
        const scale = Math.max(48 / img.width, 48 / img.height)
        const x = (48 - img.width * scale) / 2
        const y = (48 - img.height * scale) / 2
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale)
        canvas.toBlob(
          (blob) => addPhoto(blob ? URL.createObjectURL(blob) : null),
          'image/jpeg',
          0.8
        )
      }
      img.src = url
    }
  }

  const handlePostCaptureClose = (takenAt: string) => {
    setShowPostCapturePopup(false)
    if (
      currentPhotoTempId !== null &&
      !isVoiceRecordingOpenRef.current &&
      !isTextInputOpenRef.current &&
      !isTextInputSavingRef.current
    ) {
      const pid = currentPhotoTempId
      setCurrentPhotoTempId(null)
      uploadPhotoImmediately(pid, null, undefined, takenAt).catch(console.error)
    }
  }

  const cleanupAllPhotos = async () => {
    if (capturedPhotos.length > 0) {
      await Promise.allSettled(
        capturedPhotos.map((p) =>
          uploadPhotoImmediately(p.tempId, null, undefined, p.takenAt).catch(console.error)
        )
      )
      capturedPhotos.forEach((p) => {
        URL.revokeObjectURL(p.url)
        if (p.thumbnailUrl) URL.revokeObjectURL(p.thumbnailUrl)
      })
      if (lastSavedThumbnailUrl) {
        URL.revokeObjectURL(lastSavedThumbnailUrl)
        setLastSavedThumbnailUrl(null)
      }
      setCapturedPhotos([])
      setCurrentPhotoTempId(null)
      setShowPostCapturePopup(false)
      setRefreshKey((prev) => prev + 1)
    }
  }

  const handlePostCaptureVoiceRecord = (takenAt: string) => {
    currentTakenAtRef.current = takenAt
    setShowPostCapturePopup(false)
    setIsVoiceRecordingOpen(true)
  }

  const handlePostCaptureTextInput = (takenAt: string) => {
    currentTakenAtRef.current = takenAt
    setShowPostCapturePopup(false)
    setIsTextInputOpen(true)
  }

  const handleVoiceRecordingSave = async (audioBlob: Blob) => {
    setIsVoiceRecordingOpen(false)
    if (currentPhotoTempId === null) return
    const photoTempId = currentPhotoTempId
    setCurrentPhotoTempId(null)
    let uploadedId: number | undefined
    try {
      uploadedId = await uploadPhotoImmediately(
        photoTempId,
        null,
        undefined,
        currentTakenAtRef.current
      )
    } catch {
      return
    }
    if (!uploadedId) return
    try {
      const audioFile = new File([audioBlob], 'recording.webm', {
        type: audioBlob.type || 'audio/webm',
      })
      const formData = new FormData()
      formData.append('audio', audioFile)
      const result = await authFetchJson<any>('/ai/transcribe', { method: 'POST', body: formData })
      if (result.success && result.data?.transcription) {
        await updateGroupPhoto(uploadedId, { description: result.data.transcription })
        if (!isCameraOpenRef.current && !isVoiceRecordingOpenRef.current && !isTextInputOpenRef.current) {
          setRefreshKey((prev) => prev + 1)
        }
      }
    } catch (err) {
      console.error('Transcription error:', err)
    }
  }

  const handleTextInputSave = async (text: string) => {
    isTextInputSavingRef.current = true
    setIsTextInputOpen(false)
    if (currentPhotoTempId === null) {
      isTextInputSavingRef.current = false
      return
    }
    const photoTempId = currentPhotoTempId
    setCurrentPhotoTempId(null)
    uploadPhotoImmediately(photoTempId, text, undefined, currentTakenAtRef.current)
      .catch(console.error)
      .finally(() => {
        isTextInputSavingRef.current = false
      })
  }

  const timelineDays = getTimelineDays()

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex justify-center md:justify-end items-center gap-2">
          <Button onClick={() => setIsCameraOpen(true)} className="gap-2 bg-primary hover:bg-primary/90 text-white" size="sm">
            <Camera className="w-3 h-3" />
            Take
          </Button>
          <Button onClick={handleUploadButtonClick} size="sm" className="gap-2">
            <PlusIcon className="w-3 h-3" />
            Upload
          </Button>
        </div>
        <div className="text-center py-12 text-muted-foreground">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p>Loading photos...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex justify-center md:justify-end items-center gap-2">
          <Button onClick={() => setIsCameraOpen(true)} className="gap-2 bg-primary hover:bg-primary/90 text-white" size="sm">
            <Camera className="w-3 h-3" />
            Take
          </Button>
          <Button onClick={handleUploadButtonClick} size="sm" className="gap-2">
            <PlusIcon className="w-3 h-3" />
            Upload
          </Button>
        </div>
        <div className="text-center py-12 text-red-500">
          <p>Error loading photos: {error}</p>
        </div>
      </div>
    )
  }

  return (
    <GroupPhotosTimelineTabContent
      groupId={groupId}
      fileInputRef={fileInputRef}
      timelineDays={timelineDays}
      photoTagFilter={photoTagFilter}
      setPhotoTagFilter={setPhotoTagFilter}
      setIsCameraOpen={setIsCameraOpen}
      handleUploadButtonClick={handleUploadButtonClick}
      handlePhotoClick={handlePhotoClick}
      handleFileSelection={handleFileSelection}
      isModalOpen={isModalOpen}
      setIsModalOpen={setIsModalOpen}
      setPreSelectedFiles={setPreSelectedFiles}
      handleUploadSuccess={handleUploadSuccess}
      preSelectedFiles={preSelectedFiles}
      selectedPhoto={selectedPhoto}
      setSelectedPhoto={setSelectedPhoto}
      selectedVideo={selectedVideo}
      setSelectedVideo={setSelectedVideo}
      pendingViewerDescription={pendingViewerDescription}
      setPendingViewerDescription={setPendingViewerDescription}
      isViewerVoiceOpen={isViewerVoiceOpen}
      setIsViewerVoiceOpen={setIsViewerVoiceOpen}
      isViewerTextOpen={isViewerTextOpen}
      setIsViewerTextOpen={setIsViewerTextOpen}
      isViewerTranscribing={isViewerTranscribing}
      handleViewerVoiceSave={handleViewerVoiceSave}
      handleViewerTextSave={handleViewerTextSave}
      formatDate={formatDate}
      closePhotoViewer={closePhotoViewer}
      canDownload={canDownload}
      canViewConfidential={!isVolunteerRole}
      canDeletePhoto={canDeletePhoto}
      canEditDescription={canEditDescription}
      setRefreshKey={setRefreshKey}
      isCameraOpen={isCameraOpen}
      cleanupAllPhotos={cleanupAllPhotos}
      handleCameraCapture={handleCameraCapture}
      capturedPhotos={capturedPhotos}
      lastSavedThumbnailUrl={lastSavedThumbnailUrl}
      showPostCapturePopup={showPostCapturePopup}
      isVoiceRecordingOpen={isVoiceRecordingOpen}
      setIsVoiceRecordingOpen={setIsVoiceRecordingOpen}
      isTextInputOpen={isTextInputOpen}
      setIsTextInputOpen={setIsTextInputOpen}
      currentPhotoTempId={currentPhotoTempId}
      handlePostCaptureVoiceRecord={handlePostCaptureVoiceRecord}
      handlePostCaptureTextInput={handlePostCaptureTextInput}
      handlePostCaptureClose={handlePostCaptureClose}
      onPostCaptureTakenAtChange={(tempId, takenAt) =>
        setCapturedPhotos((prev) =>
          prev.map((p) => (p.tempId === tempId ? { ...p, takenAt } : p))
        )
      }
      handleVoiceRecordingSave={handleVoiceRecordingSave}
      handleTextInputSave={handleTextInputSave}
      isTextInputSavingRef={isTextInputSavingRef}
    />
  )
}
